import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowUp, BookOpen, ChevronDown, ChevronUp, Sparkles, Users } from "lucide-react";
import { api, type CompanyDocument, type CompanyGuideAnswer } from "../api";
import { BlockRenderer } from "../components/block-editor";
import { Loading } from "../components/ui";
import { RuleFiles } from "./CompanyRules";

const suggestions = ["출장 경비는 어떻게 정산하나요?", "비품 구매 절차와 문의할 팀을 알려주세요.", "연구 자료를 공유하기 전에 무엇을 확인하나요?"];
const message = (reason: unknown) => reason instanceof Error ? reason.message : "답변을 불러오지 못했습니다. 다시 시도해 주세요.";

export function WorkGuideAssistant() {
  const [question, setQuestion] = useState("");
  const [asked, setAsked] = useState("");
  const [result, setResult] = useState<CompanyGuideAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);

  async function submit(event: FormEvent) {
    event.preventDefault(); if (busy) return;
    if (!question.trim()) { setError("궁금한 업무나 담당자에 대한 질문을 입력해 주세요."); return; }
    const controller = new AbortController(); request.current = controller;
    setBusy(true); setError(""); setResult(null); setPreview(null); setAsked(question.trim());
    try { const answer = await api.askCompanyGuide(question.trim(), controller.signal); if (!controller.signal.aborted) setResult(answer); }
    catch (reason) { if (!controller.signal.aborted) setError(message(reason)); }
    finally { if (!controller.signal.aborted) { setBusy(false); request.current = null; } }
  }

  return <section className="work-guide-ai" aria-labelledby="work-guide-ai-title">
    <header><span className="work-guide-ai-mark"><Sparkles size={20} /></span><div><h2 id="work-guide-ai-title">업무가 막힐 때, AI에게 물어보세요</h2><p>공개된 업무 가이드를 찾고, 필요하면 구성원의 소속과 역할을 확인해 답해 드립니다.</p></div></header>
    <form onSubmit={submit}>
      <label htmlFor="work-guide-question">궁금한 업무 <span>질문마다 새로 검색합니다</span></label>
      <div className="work-guide-ai-input"><textarea id="work-guide-question" rows={2} maxLength={2000} placeholder="예: 출장 정산에 필요한 서류와 문의할 팀이 궁금해요." value={question} disabled={busy} onChange={event => setQuestion(event.target.value)} />
        <button type="submit" className="primary" disabled={busy}><ArrowUp size={16} />{busy ? "확인 중" : "질문하기"}</button></div>
      <div className="work-guide-ai-suggestions">{suggestions.map(value => <button type="button" key={value} disabled={busy} onClick={() => { setQuestion(value); document.getElementById("work-guide-question")?.focus(); }}>{value}</button>)}</div>
    </form>
    {error && <p className="error-box" role="alert">{error}</p>}
    <div aria-live="polite" aria-busy={busy}>
      {busy && <Loading layout="inline" label="업무 가이드와 필요한 역할 정보를 확인하고 있습니다…" />}
      {result && <div className="work-guide-ai-result"><p className="work-guide-ai-asked">{asked}</p><h3><Sparkles size={16} />AI 답변</h3><div className="work-guide-ai-answer">{result.answer}</div>
        {(result.guides.length > 0 || result.people.length > 0) && <section className="work-guide-ai-sources"><h4>참고한 자료와 구성원</h4>
          <div className="work-guide-ai-source-list">{result.guides.map(guide => <button type="button" key={guide.id} aria-expanded={preview === guide.id} aria-controls="work-guide-ai-preview" onClick={() => setPreview(value => value === guide.id ? null : guide.id)}><BookOpen size={16} /><span>{guide.title}{" "}<small>문서 버전 {guide.version} · 이 화면에서 보기</small></span>{preview === guide.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</button>)}
            {result.people.map(person => <div className="work-guide-ai-person" key={person.id}><Users size={16} /><div><strong>{person.name}</strong><span>{[person.jobTitle, person.jobLevel].filter(Boolean).join(" · ") || "직책·직급 미등록"}</span><small>{person.departments.join(", ") || "소속 미등록"}</small></div></div>)}
          </div>
          <div id="work-guide-ai-preview">{preview && <GuidePreview key={preview} id={preview} onClose={() => setPreview(null)} />}</div>
        </section>}
        <p className="work-guide-ai-note">AI 답변은 부정확할 수 있습니다. 중요한 절차는 원문을 확인해 주세요. 직책과 소속만으로 실제 담당 업무를 확정하지 않습니다.</p>
      </div>}
    </div>
  </section>;
}

function GuidePreview({ id, onClose }: { id: string; onClose: () => void }) {
  const [doc, setDoc] = useState<CompanyDocument | null>(null); const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void api.companyDocument(id, false).then(value => { if (active) setDoc(value); }).catch(reason => { if (active) setError(message(reason)); });
    return () => { active = false; };
  }, [id]);
  return <article className="work-guide-ai-preview"><header><h4>{doc?.document.title || "참고 가이드"}</h4><button type="button" onClick={onClose}>접기</button></header>
    {error && <p role="alert" className="error-box">{error}</p>}{!doc && !error && <Loading layout="detail" label="가이드 원문을 불러오는 중" />}
    {doc && <><BlockRenderer blocks={doc.blocks} loadImage={api.downloadFile} />{!!doc.files.length && <RuleFiles files={doc.files} onError={setError} />}</>}
  </article>;
}
